import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import addn from "../ADMIN/Images/add_n.gif";
import addo from "../ADMIN/Images/add_o.gif";
import editn from "../ADMIN/Images/edit_n.gif";
import edito from "../ADMIN/Images/edit_o.gif";
import closen from "../ADMIN/Images/close_n.gif";
import closeo from "../ADMIN/Images/close_o.gif";
import modifyn from "../ADMIN/Images/modify_n.gif";
import modifyo from "../ADMIN/Images/modify_o.gif";
import Loading from "../../Common/Loading"

interface TAUser {
    SchoolCode: string;
    FamilyCode: string;
    Login: string;
    Category: string;
    FirstName: string;
    LastName: string;
    Status: string;
    ExpiryDate: string;
    Email: string;
}

type SortDir = 'asc' | 'desc' | null;
interface SortState { colKey: keyof TAUser | null; dir: SortDir; }

// Added exact pixel widths to match the snug look of the live interface
const COLUMNS: Array<{ key: keyof TAUser; label: string; align: 'left' | 'right' | 'center'; width: number }> = [
    { key: 'SchoolCode', label: 'SchoolCode',  align: 'left',   width: 85 },
    { key: 'FamilyCode', label: 'FamilyCode',  align: 'left',   width: 90 },
    { key: 'Login',      label: 'Login',       align: 'left',   width: 120 },
    { key: 'Category',   label: 'Category',    align: 'left',   width: 80 },
    { key: 'FirstName',  label: 'First name',  align: 'left',   width: 120 },
    { key: 'LastName',   label: 'Last Name',   align: 'left',   width: 120 },
    { key: 'Status',     label: 'Status',      align: 'left',   width: 65 },
    { key: 'ExpiryDate', label: 'Expiry Date', align: 'left',   width: 110 },
    { key: 'Email',      label: 'Email',       align: 'left',   width: 250 }, // Base layout fallback width
];

const SERIAL_COL_WIDTH = 45;
// Calculate minimum table container width dynamically based on column configurations
const MIN_TABLE_WIDTH = SERIAL_COL_WIDTH + COLUMNS.reduce((acc, col) => acc + col.width, 0);

const HEADER_BG      = '#C7DDFD';
const HEADER_TEXT    = '#2859ac';
const HEADER_BORDER  = '#80A0FF';
const GRIDLINE_COLOR = '#A3C0E8';
const ROW_BG_ODD     = '#F4FAFF';
const ROW_BG_EVEN    = '#FFFFFF';
const SEL_BG         = 'orange';
const SEL_FG         = 'white';
const FONT_FAMILY    = 'Arial';
const FONT_SIZE      = '10pt';
const CELL_PADDING   = '8px 8px'; // Slightly reduced padding to match compact live UI row height

interface GifButtonProps {
    imgNormal: string;
    imgHover: string;
    alt: string;
    onClick: () => void;
    style?: React.CSSProperties;
}

const GifButton: React.FC<GifButtonProps> = ({ imgNormal, imgHover, alt, onClick, style }) => {
    const [hover, setHover] = useState(false);
    return (
        <img
            src={hover ? imgHover : imgNormal}
            alt={alt}
            draggable={false}
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{ cursor: 'pointer', verticalAlign: 'middle', ...style }}
        />
    );
};

function sortData(data: TAUser[], sort: SortState): TAUser[] {
    if (!sort.colKey || !sort.dir) return data;
    const key = sort.colKey;
    return [...data].sort((a, b) => {
        const av = (a[key] ?? '').toString().toLowerCase();
        const bv = (b[key] ?? '').toString().toLowerCase();
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sort.dir === 'asc' ? cmp : -cmp;
    });
}

const SortIcon: React.FC<{ dir: SortDir }> = ({ dir }) => (
    <span style={{ marginLeft: 4, fontSize: 9, opacity: dir ? 1 : 0.35 }}>
        {dir === 'desc' ? '▼' : '▲'}
    </span>
);


const thStyle: React.CSSProperties = {
    fontFamily: FONT_FAMILY,
    fontSize: FONT_SIZE,
    color: HEADER_TEXT,
    backgroundColor: HEADER_BG,
    padding: CELL_PADDING,
    whiteSpace: 'nowrap',
    verticalAlign: 'bottom',
    userSelect: 'none',
    fontWeight: 'bold',
};

const tdStyle: React.CSSProperties = {
    fontFamily: FONT_FAMILY,
    fontSize: FONT_SIZE,
    padding: CELL_PADDING,
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};


const EL_ListTAUsers: React.FC = () => {
    const API_BASE = process.env.REACT_APP_DOCUMENT_LINK ?? '';
    const navigate = useNavigate();

    const [rows, setRows]               = useState<TAUser[]>([]);
    const [loading, setLoading]         = useState(true);
    const [errorMsg, setErrorMsg]       = useState('');
    const [selectedRow, setSelectedRow] = useState<number | null>(null);
    const [sort, setSort]               = useState<SortState>({ colKey: null, dir: null });
    const [isSysAdmin, setIsSysAdmin]   = useState(false);

    const tableBodyRef = useRef<HTMLDivElement>(null);

    // ── Fetch (mirrors fn_CreateTable) ────────────────────────────────────
    useEffect(() => {
        document.body.style.backgroundColor = 'white';
        setLoading(true);
        Promise.all([
            fetch(`${API_BASE}api/ta-users/list/`).then(r => r.json()),
            fetch(`${API_BASE}api/ta-users/is-sysadmin/`).then(r => r.json()),
        ])
            .then(([listRes, adminRes]) => {
                if (listRes.success) setRows(listRes.data);
                else setErrorMsg(listRes.error ?? 'Failed to load data.');
                setIsSysAdmin(adminRes.is_sysadmin ?? false);
            })
            .catch(err => setErrorMsg(String(err)))
            .finally(() => setLoading(false));
    }, [API_BASE]);

    const displayRows  = useMemo(() => sortData(rows, sort), [rows, sort]);
    const selectedUser = selectedRow !== null ? displayRows[selectedRow - 1] : null;

    // ── Sort (mirrors fn_Sort + fn_ClearSelection) ────────────────────────
    const handleSort = useCallback((key: keyof TAUser) => {
        setSelectedRow(null);
        setSort(prev => {
            if (prev.colKey !== key) return { colKey: key, dir: 'asc' };
            if (prev.dir === 'asc')  return { colKey: key, dir: 'desc' };
            return { colKey: null, dir: null };
        });
    }, []);

    // ── Row click (mirrors fn_SelectRow 'S', m_CanUnSelect = false) ───────
    const handleRowClick = useCallback((rowNo: number) => {
        setSelectedRow(rowNo);
    }, []);
    const handleAdd = useCallback(() => {
        navigate('/WEBADMIN/ADMIN/EL_EditLogin?SchoolCode=A&FamilyCode=AA&CalledFrom=TA');
    }, [navigate]);

    const handleModify = useCallback(() => {
        if (!selectedUser) {
            alert('Please Select a row.');
            return;
        }
        navigate(
            `/WEBADMIN/ADMIN/EL_EditLogin?SchoolCode=A&FamilyCode=${selectedUser.FamilyCode}&Login=${selectedUser.Login}&CalledFrom=TA`
        );
    }, [selectedUser, navigate]);

    const handleRoles = useCallback(() => {
        if (!selectedUser) {
            alert('Please Select a row.');
            return;
        }
        navigate(
            `/WEBADMIN/ADMIN/EL_TutorAdminRoles?S=A&F=${selectedUser.FamilyCode}&L=${selectedUser.Login}`
        );
    }, [selectedUser, navigate]);

    const handleClose = useCallback(() => {
        navigate('/SysAdmin');
    }, [navigate]);

    useEffect(() => {
        if (errorMsg) alert(errorMsg);
    }, [errorMsg]);


    return (
        <div
            id="divMain"
            style={{
                width: '100%',
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: FONT_FAMILY,
                fontSize: FONT_SIZE,
                boxSizing: 'border-box',
                overflow: 'hidden',
            }}
        >
            {/* ── Grid container (mirrors login_divScrollableTable) ── */}
            <div
                id="login_divScrollableTable"
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    border: `1px solid ${HEADER_BORDER}`,
                }}
            >
                {/* Title bar (mirrors divTitle) */}
                <div
                    id="login_divTitle"
                    style={{
                        backgroundColor: HEADER_BG,
                        color: HEADER_TEXT,
                        fontFamily: FONT_FAMILY,
                        fontSize: FONT_SIZE,
                        fontWeight: 'bold',
                        padding: CELL_PADDING,
                        borderBottom: `1px solid ${HEADER_BORDER}`,
                        textAlign: 'center',
                        userSelect: 'none',
                    }}
                >
                    Tutor Admin Users List
                </div>

                {/* Sticky column header (mirrors login_divHead / ConstructHeading) */}
                <div
                    id="login_divHead"
                    style={{
                        backgroundColor: HEADER_BG,
                        borderBottom: `1px solid ${HEADER_BORDER}`,
                        overflowX: 'hidden',
                        flexShrink: 0,
                    }}
                >
                    <table
                        id="login_tblHead"
                        style={{ 
                            width: '100%', 
                            minWidth: `${MIN_TABLE_WIDTH}px`, 
                            borderCollapse: 'collapse', 
                            tableLayout: 'fixed' 
                        }}
                    >
                        <colgroup>
                            <col style={{ width: SERIAL_COL_WIDTH }} />
                            {COLUMNS.map((c, idx) => {
                                // Leave the last column (Email) dynamic to absorb remaining right-side space
                                if (idx === COLUMNS.length - 1) return <col key={c.key} />;
                                return <col key={c.key} style={{ width: c.width }} />;
                            })}
                        </colgroup>
                        <thead>
                            <tr id="login_trh">
                                {/* # serial number header */}
                                <th style={{ ...thStyle, textAlign: 'center', width: SERIAL_COL_WIDTH, cursor: 'default' }}>
                                    #
                                </th>
                                {COLUMNS.map(col => (
                                    <th
                                        key={col.key}
                                        onClick={() => handleSort(col.key)}
                                        style={{
                                            ...thStyle,
                                            textAlign: col.align,
                                            cursor: 'pointer',
                                            borderLeft: `1px solid ${GRIDLINE_COLOR}`,
                                        }}
                                        title={`Sort by ${col.label}`}
                                    >
                                        {col.label}
                                        <SortIcon dir={sort.colKey === col.key ? sort.dir : null} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                    </table>
                </div>

                {/* Scrollable body (mirrors login_divCont) */}
                <div
                    id="login_divCont"
                    ref={tableBodyRef}
                    style={{
                        flex: 1,
                        overflowY: 'scroll',
                        overflowX: 'auto',
                        borderTop: `1px solid ${HEADER_BORDER}`,
                    }}
                    onScroll={e => {
                        // Sync header horizontal scroll (mirrors fn_Scroll)
                        const head = document.getElementById('login_divHead');
                        if (head) head.scrollLeft = (e.target as HTMLElement).scrollLeft;
                    }}
                >
                    {loading ? (
                        <Loading />
                    ) : (
                        <table
                            id="login_tblCont"
                            style={{ 
                                width: '100%', 
                                minWidth: `${MIN_TABLE_WIDTH}px`, 
                                borderCollapse: 'collapse', 
                                tableLayout: 'fixed' 
                            }}
                        >
                            <colgroup>
                                <col style={{ width: SERIAL_COL_WIDTH }} />
                                {COLUMNS.map((c, idx) => {
                                    // Leave the last column (Email) dynamic to absorb remaining right-side space
                                    if (idx === COLUMNS.length - 1) return <col key={c.key} />;
                                    return <col key={c.key} style={{ width: c.width }} />;
                                })}
                            </colgroup>
                            <tbody>
                                {displayRows.map((row, idx) => {
                                    const rowNo = idx + 1;
                                    const isSelected = selectedRow === rowNo;
                                    // Alternating colours mirrors: IIf(pRowNo Mod 2 = 1, m_RowBackColor, m_RowAltBackColor)
                                    const bgColor = isSelected ? SEL_BG  : rowNo % 2 === 1 ? ROW_BG_ODD : ROW_BG_EVEN;
                                    const fgColor = isSelected ? SEL_FG  : 'black';

                                    return (
                                        <tr
                                            key={rowNo}
                                            id={`login_tr${rowNo}`}
                                            onClick={() => handleRowClick(rowNo)}
                                            style={{ backgroundColor: bgColor, color: fgColor, cursor: 'pointer' }}
                                        >
                                            {/* Serial number cell */}
                                            <td style={{ ...tdStyle, textAlign: 'center', width: SERIAL_COL_WIDTH, borderLeft: 'none' }}>
                                                {rowNo}
                                            </td>
                                            {COLUMNS.map((col, ci) => (
                                                <td
                                                    key={col.key}
                                                    style={{
                                                        ...tdStyle,
                                                        textAlign: col.align,
                                                        borderLeft: `1px solid ${GRIDLINE_COLOR}`,
                                                    }}
                                                    title={String(row[col.key] ?? '')}
                                                >
                                                    {String(row[col.key] ?? '').trim() || '\u00a0'}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}

                                {!loading && displayRows.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={COLUMNS.length + 1}
                                            style={{ textAlign: 'center', padding: 12, color: '#888' }}
                                        >
                                            No records found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* ── Button bar (mirrors divButtons) ── */}
            <div
                id="divButtons"
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 40,
                    padding: '10px 0',
                    flexShrink: 0,
                    backgroundColor: '#fff',
                    borderTop: `1px solid ${GRIDLINE_COLOR}`,
                }}
            >
                {/* imgAdd */}
                <GifButton
                    imgNormal={addn}
                    imgHover={addo}
                    alt="Add"
                    onClick={handleAdd}
                />

                {/* imgBtnModify */}
                <GifButton
                    imgNormal={modifyn}
                    imgHover={modifyo}
                    alt="Modify"
                    onClick={handleModify}
                />

                {/* imgBtnRoles – visible only when isSysAdmin (mirrors imgBtnRoles.Visible = True) */}
                {/* {isSysAdmin && ( */}
                    <GifButton
                        imgNormal={editn}
                        imgHover={edito}
                        alt="Edit"
                        onClick={handleRoles}
                    />
                {/* )} */}

                {/* imgBtnClose */}
                <GifButton
                    imgNormal={closen}
                    imgHover={closeo}
                    alt="Close"
                    onClick={handleClose}
                />
            </div>
        </div>
    );
};

export default EL_ListTAUsers;